import React, { useRef, useState } from 'react';
import { ButtonRounded, Notification } from 'is24-corecss';
import { IconS24ArrowLeft24, IconS24Close24 } from '@is24/cosma-ui-icons';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiResponseError } from '../../api/ApiResponseError';
import { startWebIdAutoIdent } from '../../api/webid/webIdVerification';
import { withTvsState, toUrl as redirectToUrl } from '../../utils/redirect';
import { useClientConfig } from '../../utils/clientConfigContext';
import { useTvsState } from '../../utils/useTvsState';
import IdentificationInfoContent from '../../components/identificationInfoContent/IdentificationInfoContent';
import InfoPopOver from '../../components/infoPopOver/InfoPopOver';
import webIdLogo from '../../components/webIdVerification/webid-logo.svg';
import { isMobileDevice } from '../../utils/isMobileDevice';

interface WebIdVerificationStartPageProps {
  onDesktopHandoff?: (handoff: { redirectUrl: string; sessionId: string; sourceSessionId: string } | null) => void;
}

const getResultPageUrl = (result: 'success' | 'failure') =>
  new URL(
    `${process.env.PUBLIC_URL || '/'}verification-${result}.html`,
    process.env.TVS_SERVICE_BASE_URL || window.location.origin,
  ).toString();

const WebIdVerificationStartPage: React.FC<WebIdVerificationStartPageProps> = ({ onDesktopHandoff }) => {
  const { t } = useTranslation();
  const { baseUrl } = useClientConfig();
  const [, setTvsState] = useTvsState();
  const [searchParams] = useSearchParams();
  const [isRequestPending, setIsRequestPending] = useState(false);
  const isStartRequestInFlight = useRef(false);
  const [requestError, setRequestError] = useState('');
  const sessionId = searchParams.get('webid_session_id');

  const onStartClick = async () => {
    if (isStartRequestInFlight.current) return;
    if (!sessionId) {
      setRequestError(t('errors.webid.bad.request' as any));
      return;
    }

    isStartRequestInFlight.current = true;
    setIsRequestPending(true);
    setRequestError('');
    const isDesktop = !isMobileDevice();

    try {
      const response = await startWebIdAutoIdent({
        sessionId,
        webIdTermsConfirmed: true,
        redirectUrl: isDesktop ? getResultPageUrl('success') : withTvsState(baseUrl, 'webid_verification_check_page'),
        redirectDeclineUrl: isDesktop
          ? getResultPageUrl('failure')
          : withTvsState(baseUrl, 'verification_failure_webid'),
      });
      if (isDesktop && onDesktopHandoff) {
        onDesktopHandoff({
          redirectUrl: response.redirectUrl,
          sessionId: response.sessionId,
          sourceSessionId: sessionId,
        });
      } else {
        redirectToUrl(response.redirectUrl);
      }
    } catch (error) {
      if (error instanceof ApiResponseError && error.responseStatus === 400) {
        setRequestError(t('errors.webid.bad.request' as any));
      } else if (error instanceof ApiResponseError && error.responseStatus === 401) {
        setRequestError(t('errors.unauthorized'));
      } else {
        setRequestError(t('errors.default'));
      }
    } finally {
      isStartRequestInFlight.current = false;
      setIsRequestPending(false);
    }
  };

  const goBackToLandingPage = () => {
    setTvsState('verification_landing_page');
  };

  const onClose = () => {
    setTvsState('');
  };

  return (
    <div aria-modal='true' className='modal-backdrop' style={{ zIndex: 2000 }}>
      <div className='modal-container' style={{ width: 601 }}>
        <div className='modal-title-wrapper'>
          <h4 className='modal-headline grid-flex'>
            <IconS24ArrowLeft24
              onClick={goBackToLandingPage}
              className='align-center grid-align-center margin-right-xxl'
            />
            {t('WebIdVerificationStartPage.heading')}
          </h4>
          <IconS24Close24 onClick={onClose} data-testid='modal-close-icon' className='tvs-close-button' />
        </div>
        <div className='modal-content-wrapper display-flex margin-top-m'>
          <div className='modal-content palm-display-flex'>
            {requestError && (
              <Notification
                data-testid='error-message'
                className='margin-bottom-xxl'
                title={t('VerificationLandingPage.errorHeading')}
                message={requestError}
                type='error'
                variant='flat'
              />
            )}
            <div className='grid grid-flex margin-bottom-m'>
              <IdentificationInfoContent informationText={'WebIdVerificationStartPage.body'} />
            </div>
            <div className='margin-top-xl'>
              <div className='font-heading-xsmall-bold'>{t('WebIdVerificationStartPage.nextHeading')}</div>
              <ul style={{ listStyleType: 'disc' }} className='padding-left-l padding-bottom-xl'>
                <li className='margin-top-m font-heading-xsmall-bold'>
                  {t('WebIdVerificationStartPage.nextStepBrowser')}
                </li>
                <li className='margin-top-s font-heading-xsmall-bold'>
                  {t('WebIdVerificationStartPage.nextStepDocument')}
                </li>
                <li className='margin-top-s font-heading-xsmall-bold'>
                  {t('WebIdVerificationStartPage.nextStepDuration')}
                </li>
              </ul>
            </div>
            <div className='grid grid-flex font-body-small-regular  font-lightgray   margin-top-xxl palm-margin-top-xl  palm-margin-bottom-l'>
              {t('WebIdVerificationStartPage.redirectWarning')}
            </div>
          </div>
        </div>
        <div className='modal-footer verification-modal-footer'>
          <div className='grid grid-flex'>
            <div className='grid-item grid-align-center one-half palm-one-whole'>
              <div className='grid grid-flex grid-align-center palm-padding-bottom-l'>
                <p className='margin-right-xs font-body-small-regular font-lightgray'>
                  {t('VerificationLandingPage.footerText')}
                </p>
                <img src={webIdLogo} alt='verification' className='footer-icon' height={13} width={49.48} />
                <InfoPopOver content={t('InformationText.webIdPopOverContent' as any, { returnObjects: true })} />
              </div>
            </div>
            <div className='grid-item one-half palm-one-whole'>
              <div className='grid-flex grid-justify-end verification-footer-div palm-one-whole'>
                <ButtonRounded
                  data-testid='webid-start-verification-button'
                  className='verification-footer-button'
                  isLoading={isRequestPending}
                  size='medium'
                  onClick={onStartClick}
                  disabled={!sessionId || isRequestPending}
                >
                  {t('WebIdVerificationStartPage.buttonText')}
                </ButtonRounded>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebIdVerificationStartPage;
